import { onBeforeUnmount, watch, type ComputedRef, type Ref } from 'vue';
import type {
  ConvertEntitiesState,
  SketchEntitiesTransformInputMode,
  SketchEntitiesTransformSelectionRole,
  SketchEntitiesTransformState,
  SketchCornerMode,
  SketchTrimEntityRef,
  SketchTrimMode,
  SketchTrimOptions,
} from '@/core';
import {
  SketchDrawingTool,
  SketchPropertyEditService,
  createSketchPropertyHistorySessionService,
  type SketchPropertyHistoryCommitter,
  type SketchPropertyHistoryFailureReporter,
  type InsertComponentFixationMode,
  type Viewer,
  type ViewerMode,
  type CoverType,
} from '@puppycad/cad-core';
import type { RevolveUiState } from '@/composables/revolve/useRevolveFeatureTypes';
import type { ExtrudeUiState } from '@/composables/useExtrudeFeature';
import type { FileInfo } from '@puppycad/proto'

/** 草图属性编辑的用户可读历史标签。 */
const SKETCH_PROPERTY_HISTORY_LABEL = '编辑草图属性';

/** 草图属性编辑的模型历史命令类型。 */
const SKETCH_PROPERTY_HISTORY_COMMAND_TYPE = 'sketch.property.edit';

/**
 * 属性面板上下文构建参数。
 */
export interface UseEditorPropertyManagerContextOptions {
  /** 当前激活命令。 */
  activeOperationCommand: Ref<string | null>;
  /** 当前绘图工具。 */
  currentDrawingCommand: Ref<string | null>;
  currentDrawingTool: Ref<string | null>;
  /** 是否处于绘图模式。 */
  isDrawingMode: Ref<boolean>;
  /** 是否处于草图环境。 */
  isSketchActive: Ref<boolean>;
  /** 是否处于历史版本只读预览态。 */
  readonlyPreview: Ref<boolean> | ComputedRef<boolean>;
  /** 当前草图基准面 ID。 */
  currentDrawingDatumPlaneId: Ref<number | string>;
  /** 当前选中对象。 */
  selectedObject: Ref<unknown>;
  /** NaiveUI 确认弹窗实例。 */
  dialog?: {
    /** 创建确认弹窗。 */
    create: (options: Record<string, unknown>) => unknown;
  };
  /**
   * 读取当前属性面板真实正在编辑的草图运行时对象。
   *
   * 设计说明：
   * 1. 设计树 selectedObject 不一定等于草图面板正在修改的 Object3D；
   * 2. 这里通过函数读取选区 store，避免把 three.js 对象放进 Vue 响应式状态；
   * 3. 属性历史 before 快照必须以该对象为准，否则属性 Undo 会误撤销上一条绘制事务。
   */
  getSketchPropertySelectedObject?: () => unknown;
  /** 读取当前视口运行时句柄。 */
  getViewer: () => Viewer | null;
  /** 当前选中的草图尺寸（尺寸属性 section 可见性判据，数据由 section 自拉）。 */
  sketchDimensionSelection?: Ref<{sketchId: string; dimensionId: string} | null>;
  /** 提交草图属性编辑产生的运行时历史记录。 */
  commitSketchRuntimeHistoryRecord?: SketchPropertyHistoryCommitter;
  /** 上报草图属性历史提交失败。 */
  reportSketchPropertyHistoryFailure?: SketchPropertyHistoryFailureReporter;
  /** 拉伸 UI 状态。 */
  extrudeUiState: Ref<ExtrudeUiState>;
  /** 旋转 UI 状态。 */
  revolveUiState: ComputedRef<RevolveUiState>;
  /** 旋转切除 UI 状态。 */
  revolveCutUiState: ComputedRef<RevolveUiState>;
  /** 拉伸提交。 */
  submitExtrude: () => Promise<void>;
  /** 拉伸取消。 */
  cancelExtrude: () => boolean;
  /** 设置拉伸深度。 */
  setExtrudeDepth: (depth: number) => void;
  /** 设置拉伸方向。 */
  setExtrudeDirection: (direction: number | 'normal' | 'reverse') => void;
  /** 设置方向 1。 */
  setExtrudeDirection1: (definition: unknown) => void;
  /** 激活拉伸终止目标选择。 */
  setExtrudeEndTargetInputActive: (direction: 'direction1' | 'direction2' | null) => void;
  setExtrudeEndTargetVisual?: (direction: 'direction1' | 'direction2', mode: 'select' | 'hover' | 'clear') => void;
  /** 设置方向 2。 */
  setExtrudeDirection2: (definition: unknown) => void;
  /** 设置拉伸切除专属定义。 */
  setExtrudeCutOptions: (definition: unknown) => void;
  /** 设置薄壁。 */
  setExtrudeThinFeature: (definition: unknown) => void;
  /** 设置合并结果。 */
  setExtrudeMergeResult: (value: boolean) => void;
  /** 设置所选轮廓输入框激活状态。 */
  setExtrudeSelectedContoursInputActive: (active: boolean) => void;
  /** 删除拉伸轮廓。 */
  removeExtrudeSelectedContour: (id: string) => void;
  /** 高亮拉伸轮廓。 */
  highlightExtrudeSelectedContour: (id: string | null) => void;
  /** 旋转提交。 */
  submitRevolve: () => Promise<void>;
  /** 旋转取消。 */
  cancelRevolve: () => boolean;
  /** 激活旋转选择框。 */
  activateRevolvePicker: (target: 'axis' | 'profile') => void;
  /** 清空旋转轴。 */
  clearRevolveSelectedAxis: () => void;
  /** 清空旋转轮廓。 */
  clearRevolveSelectedContours: () => void;
  /** 移除旋转轮廓。 */
  removeRevolveSelectedContour: (id: string) => void;
  /** 高亮旋转轴。 */
  highlightRevolveSelectedAxis: (enabled: boolean) => void;
  /** 高亮旋转轮廓。 */
  highlightRevolveSelectedContour: (id: string | null) => void;
  /** 设置旋转方向 1 角度。 */
  setRevolveDirection1Angle: (angle: number) => void;
  /** 设置旋转方向 2 开关。 */
  setRevolveDirection2Enabled: (enabled: boolean) => void;
  /** 设置旋转方向 2 角度。 */
  setRevolveDirection2Angle: (angle: number) => void;
  /** 设置旋转反向。 */
  setRevolveDirectionReversed: (reversed: boolean) => void;
  /** 旋转切除提交。 */
  submitRevolveCut: () => Promise<void>;
  /** 旋转切除取消。 */
  cancelRevolveCut: () => boolean;
  /** 激活旋转切除选择框。 */
  activateRevolveCutPicker: (target: 'axis' | 'profile') => void;
  /** 清空旋转切除轴。 */
  clearRevolveCutSelectedAxis: () => void;
  /** 清空旋转切除轮廓。 */
  clearRevolveCutSelectedContours: () => void;
  /** 移除旋转切除轮廓。 */
  removeRevolveCutSelectedContour: (id: string) => void;
  /** 高亮旋转切除轴。 */
  highlightRevolveCutSelectedAxis: (enabled: boolean) => void;
  /** 高亮旋转切除轮廓。 */
  highlightRevolveCutSelectedContour: (id: string | null) => void;
  /** 设置旋转切除方向 1 角度。 */
  setRevolveCutDirection1Angle: (angle: number) => void;
  /** 设置旋转切除方向 2 开关。 */
  setRevolveCutDirection2Enabled: (enabled: boolean) => void;
  /** 设置旋转切除方向 2 角度。 */
  setRevolveCutDirection2Angle: (angle: number) => void;
  /** 设置旋转切除反向。 */
  setRevolveCutDirectionReversed: (reversed: boolean) => void;
  /** 设置旋转切除翻转切除侧。 */
  setRevolveCutFlipSideToCut: (enabled: boolean) => void;
  /** 切换旋转切除保留体片段。 */
  toggleRevolveCutFragment: (sourceSolidId: string, shapeKey: string) => void;
  /** 设置旋转切除保留实体弹窗选择模式。 */
  setRevolveCutKeepBodiesSelectionMode?: (mode: 'all' | 'selected') => void;
  /** 切换旋转切除保留实体弹窗草稿片段。 */
  toggleRevolveCutKeepBodiesDraftFragment?: (sourceSolidId: string, shapeKey: string) => void;
  /** 确认旋转切除保留实体弹窗。 */
  confirmRevolveCutKeepBodiesDialog?: () => void;
  /** 取消旋转切除保留实体弹窗。 */
  cancelRevolveCutKeepBodiesDialog?: () => void;
  /** 基准面命令是否激活。 */
  referencePlaneActive: Ref<boolean>;
  /** 基准轴命令是否激活。 */
  referenceAxisActive: Ref<boolean>;
  /** 基准点命令是否激活。 */
  referencePointActive: Ref<boolean>;
  /** 装配体基准面命令是否激活。 */
  assemblyReferencePlaneActive: Ref<boolean>;
  /** 装配体基准轴命令是否激活。 */
  assemblyReferenceAxisActive: Ref<boolean>;
  /** 装配体基准点命令是否激活。 */
  assemblyReferencePointActive: Ref<boolean>;
  /** 提交基准轴。 */
  submitReferenceAxis: () => Promise<void>;
  /** 取消基准轴。 */
  cancelReferenceAxis: () => void;
  /** 提交基准面。 */
  submitReferencePlane: () => Promise<void>;
  /** 取消基准面。 */
  cancelReferencePlane: () => void;
  /** 提交基准点。 */
  submitReferencePoint: () => Promise<void>;
  /** 取消基准点。 */
  cancelReferencePoint: () => void;
  /** 提交装配体基准轴。 */
  submitAssemblyReferenceAxis: () => Promise<void>;
  /** 取消装配体基准轴。 */
  cancelAssemblyReferenceAxis: () => void;
  /** 提交装配体基准面。 */
  submitAssemblyReferencePlane: () => Promise<void>;
  /** 取消装配体基准面。 */
  cancelAssemblyReferencePlane: () => void;
  /** 提交装配体基准点。 */
  submitAssemblyReferencePoint: () => Promise<void>;
  /** 取消装配体基准点。 */
  cancelAssemblyReferencePoint: () => void;
  /** 草图倒角模式。 */
  sketchCornerMode: Ref<SketchCornerMode>;
  /** 是否启用草图倒角。 */
  sketchCornerEnable: Ref<boolean>;
  /** 设置草图倒角模式。 */
  setSketchCornerMode: (mode: SketchCornerMode | null) => void;
  /** 草图修剪模式。 */
  sketchTrimMode: Ref<SketchTrimMode | null>;
  /** 草图修剪选项。 */
  sketchTrimOptions: Ref<SketchTrimOptions>;
  /** 当前选中修剪实体。 */
  selectedSketchTrimEntity: Ref<SketchTrimEntityRef | { objectId: string; segmentIndex: number | null; hitPoint?: [number, number, number] } | null | unknown>;
  /** 设置修剪模式。 */
  setSketchTrimMode: (mode: SketchTrimMode | null) => void;
  /** 设置修剪选项。 */
  setSketchTrimOptions: (options: SketchTrimOptions) => void;
  //------ 等距实体业务流程 开始------
  /** 等距实体偏移距离。 */
  sketchOffsetDistance: Ref<number>;
  /** 等距实体是否反向。 */
  sketchOffsetReverse: Ref<boolean>;
  /** 等距实体是否双向。 */
  sketchOffsetBidirectional: Ref<boolean>;
  /** 等距实体是否添加尺寸。 */
  sketchOffsetAddSize: Ref<boolean>;
  /** 等距实体是否开启选择链。 */
  sketchOffsetSelectChain: Ref<boolean>;
  /** 等距实体盖子配置。 */
  sketchOffsetCover: Ref<{ hasCover: boolean; type: CoverType }>;
  /** 等距实体是否为基本几何体。 */
  sketchOffsetBasicGeometry: Ref<boolean>;
  /** 等距实体是否为偏移几何体。 */
  sketchOffsetOffsetGeometry: Ref<boolean>;
  /** 设置等距实体距离回调。 */
  setSketchOffsetDistance: (value: number) => void;
  /** 设置等距实体方向回调。 */
  setSketchOffsetReverse: (value: boolean) => void;
  /** 设置等距实体双向回调。 */
  setSketchOffsetBidirectional: (value: boolean) => void;
  /** 设置等距实体添加尺寸回调。 */
  setSketchOffsetAddSize: (value: boolean) => void;
  /** 设置等距实体选择链回调。 */
  setSketchOffsetSelectChain: (value: boolean) => void;
  /** 设置等距实体盖子配置回调。 */
  setSketchOffsetCover: (value: { hasCover: boolean; type: CoverType }) => void;
  /** 设置等距实体基本几何体回调。 */
  setSketchOffsetBasicGeometry: (value: boolean) => void;
  /** 设置等距实体偏移几何体回调。 */
  setSketchOffsetOffsetGeometry: (value: boolean) => void;
  /** 清除等距实体工作流，退出模式。 */
  clearSketchOffsetWorkflow: () => void;
  /** 生成最终等距实体并且退出等距实体工作流 */
  generateAndExitSketchOffsetWorkflow: () => void;
  //------ 等距实体业务流程 结束------
  /** Convert Entities 命令会话状态。 */
  convertEntitiesState: Ref<ConvertEntitiesState>;
  /** 确认 Convert Entities。 */
  confirmConvertEntities: () => void | Promise<void>;
  /** 取消 Convert Entities。 */
  cancelConvertEntities: () => void;
  /** 移除单个 Convert Entities 已选实体。 */
  removeConvertEntitiesSelection: (selectionId: string) => void;
  /** 设置 Convert Entities 是否作为构造线创建。 */
  setConvertEntitiesAsConstruction: (value: boolean) => void;
  /** 设置 Convert Entities 是否启用草图链选择。 */
  setConvertEntitiesSelectChain: (value: boolean) => void;
  /** 设置 Convert Entities 是否按单个内环分别创建引用实体。 */
  setConvertEntitiesInnerLoopsOneByOne: (value: boolean) => void;
  /** 将当前已选面的全部内环加入 Convert Entities 选择集合。 */
  selectAllConvertEntitiesInnerLoops: () => void;
  //------ 草图实体变换 UI 工作流 开始------
  /** 草图实体变换命令会话状态。 */
  sketchEntitiesTransformState: Ref<SketchEntitiesTransformState>;
  /** 设置草图实体变换输入模式。 */
  setSketchEntitiesTransformInputMode: (mode: SketchEntitiesTransformInputMode) => void;
  /** 激活草图实体变换选择角色。 */
  setSketchEntitiesTransformSelectionRole: (role: SketchEntitiesTransformSelectionRole) => void;
  /** 设置草图实体变换 X/Y 目标坐标。 */
  setSketchEntitiesTransformTargetCoordinate: (axis: 'x' | 'y', value: number | null) => void;
  /** 设置草图实体旋转角度。 */
  setSketchEntitiesTransformAngle: (value: number | null) => void;
  /** 同步草图实体变换命令会话状态。 */
  syncSketchEntitiesTransformState: (state: SketchEntitiesTransformState) => void;
  /** 移除单个草图实体变换已选实体。 */
  removeSketchEntitiesTransformSelection: (selectionId: string) => void;
  /** 重复上一次草图实体变换。 */
  repeatSketchEntitiesTransform: () => void;
  /** 确认草图实体变换。 */
  confirmSketchEntitiesTransform: () => void | Promise<void>;
  /** 取消草图实体变换。 */
  cancelSketchEntitiesTransform: () => void;
  //------ 草图实体变换 UI 工作流 结束------
  /** 删除草图关系。 */
  removeSketchRelation?: (targetEntity: unknown, constraintId: number) => Promise<boolean>;
  /** 线绘图点数。 */
  lineDrawingPointCount: Ref<number>;
  /** 当前绘图点数。 */
  drawingPointCount: Ref<number>;
  /** 是否处于多实体关系面板候选态。 */
  multipleEntitiesBoxSelected: Ref<boolean>;
  /** 多选对象列表。 */
  multipleSelectedObjects: Ref<unknown[]>;
  /** 多选对象 ID 列表。 */
  multipleSelectedObjectIds: Ref<string[]>;
  /** 框选方向。 */
  multipleSelectedIsCrossing: Ref<boolean>;
  /** 各草图属性面板可见性。 */
  linePropertyVisible: Ref<boolean>;
  arcPropertyVisible: Ref<boolean>;
  circlePropertyVisible: Ref<boolean>;
  ellipsePropertyVisible: Ref<boolean>;
  polygonPropertyVisible: Ref<boolean>;
  rectanglePropertyVisible: Ref<boolean>;
  splinePropertyVisible: Ref<boolean>;
  styleSplinePropertyVisible: Ref<boolean>;
  slotPropertyVisible: Ref<boolean>;
  pointPropertyVisible: Ref<boolean>;
  insertLinePropertyVisible: Ref<boolean>;
  /** 插入零部件激活状态。 */
  insertComponentActive: Ref<boolean>;
  /** 插入零部件文件列表。 */
  insertComponentFiles: Ref<FileInfo[]>;
  /** 插入零部件筛选类型（OR 匹配多个 kind）。 */
  insertComponentFilterKinds: Ref<string[]>;
  /** 插入零部件搜索关键词。 */
  insertComponentQuery: Ref<string>;
  /** 插入零部件加载状态。 */
  insertComponentLoading: Ref<boolean>;
  /** 插入零部件是否有更多。 */
  insertComponentHasMore: Ref<boolean>;
  /** 插入零部件总数。 */
  insertComponentTotal: Ref<number>;
  /** 插入时固定/浮动零部件策略。 */
  insertComponentFixationMode: Ref<InsertComponentFixationMode>;
  /** 插入零部件 PropertyManager 中按勾选顺序保存的文件列表。 */
  selectedInsertComponentFiles: Ref<FileInfo[]>;
  /** 设置插入零部件筛选类型（OR 匹配多个 kind）。 */
  setInsertComponentFilterKinds: (kinds: string[]) => void;
  /** 设置插入零部件搜索关键词。 */
  setInsertComponentQuery: (query: string) => void;
  /** 设置插入时固定/浮动零部件策略。 */
  setInsertComponentFixationMode: (mode: InsertComponentFixationMode) => void;
  /** 加载更多插入零部件。 */
  loadMoreInsertComponents: () => void;
  /** 选择插入零部件。 */
  selectInsertComponent: (file: FileInfo) => void;
  /** 切换插入零部件勾选状态。 */
  toggleInsertComponentSelection: (file: FileInfo) => void;
  /** 确认插入零部件勾选队列。 */
  confirmInsertComponentSelection: () => boolean;
  /** 取消插入零部件。 */
  cancelInsertComponent: () => void;
  //------ 线性零部件阵列入口 开始------
  /** 确认装配体线性零部件阵列。 */
  confirmAssemblyLinearComponentPattern: () => void;
  /** 取消装配体线性零部件阵列。 */
  cancelAssemblyLinearComponentPattern: () => void;
  //------ 线性零部件阵列入口 结束------
  /** 清空当前选择。 */
  clearSelection: () => void;
  /** 取消按钮回调。 */
  handlePropertyManagerCancel: () => void;
  /** 确认按钮回调。 */
  handlePropertyManagerConfirm: () => void;
  /** 帮助按钮回调。 */
  handlePropertyManagerHelp: () => void;
  /** 预览切换回调。 */
  handlePropertyManagerPreviewToggle: () => void;
  /** 特征模块追加的动态上下文。 */
  blendContext?: Ref<Record<string, unknown>> | ComputedRef<Record<string, unknown>>;
  /** 抽壳模块追加的动态上下文。 */
  shellContext?: Ref<Record<string, unknown>> | ComputedRef<Record<string, unknown>>;
  /** 筋模块追加的动态上下文。 */
  ribContext?: Ref<Record<string, unknown>> | ComputedRef<Record<string, unknown>>;
  /**
   * 面板内主动切换当前激活命令（如移动/旋转互切），
   * 用于让 PropertyManager 标题等响应式字段实时刷新。
   */
  handleSwitchActiveCommand: (command: string) => void;
}

/**
 * 创建属性面板上下文构建器。
 *
 * 设计说明：
 * 1. `Editor.vue` 只保留模板与顶层编排，不再直接堆叠超长上下文字面量；
 * 2. 该模块只负责把现有宿主状态投影成 `PropertyManager` 可消费的数据；
 * 3. 不在这里执行任何业务逻辑，所有动作均通过外部回调下沉。
 */
export function useEditorPropertyManagerContext(options: UseEditorPropertyManagerContextOptions) {
  const sketchPropertyEditService = new SketchPropertyEditService(
    (target, operationIntents) => (
      options.getViewer()?.solveActiveSketchParametricProperties(target, operationIntents) ?? false
    ),
  );
  const sketchPropertyHistorySessionService = createSketchPropertyHistorySessionService({
    getViewer: options.getViewer,
    commitHistoryRecord: options.commitSketchRuntimeHistoryRecord,
    reportFailure: options.reportSketchPropertyHistoryFailure,
  });

  /**
   * 解析属性历史应捕获的草图对象。
   *
   * 设计说明：
   * 1. 草图属性面板的编辑目标来自各选区 store，不一定等于设计树 selectedObject；
   * 2. 优先使用显式传入目标，其次实时读取草图选区，最后才回退到设计树对象；
   * 3. 通过函数读取可以避免把 three.js 对象包装成新的 Vue 响应式数据。
   */
  const resolveSketchPropertyHistoryTarget = (explicitTarget?: unknown): unknown => (
    explicitTarget
    ?? options.getSketchPropertySelectedObject?.()
    ?? options.selectedObject.value
  );

  /**
   * 捕获草图属性编辑前的基线快照。
   *
   * @param target 可选的明确编辑对象；未传时从当前草图选区解析。
   */
  const prepareSketchPropertyEdit = (target?: unknown): void => {
    sketchPropertyHistorySessionService.prepareBaseline({
      target: resolveSketchPropertyHistoryTarget(target),
      label: SKETCH_PROPERTY_HISTORY_LABEL,
    });
  };

  /**
   * 选中草图对象时立即捕获属性编辑 before 快照。
   *
   * 设计说明：
   * 草图属性面板的历史旧链路是“面板先直接改运行时对象，再调用 finalize 收口”，
   * 因此 finalize 时已经无法再拿到 before。这里在选择进入属性面板时提前捕获基线，
   * 并且只保存快照 ID 与草图 ID，不把 Three.js 对象放入 Vue 响应式状态。
   */
  watch(
    () => options.selectedObject.value,
    () => {
      prepareSketchPropertyEdit();
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    sketchPropertyHistorySessionService.dispose();
  });
  /**
   * 完成草图属性编辑后的统一收口。
   * @param target 被属性面板修改的草图对象。
   * @param input 编辑上下文。
   */
  const finalizeSketchPropertyEdit = (
    target: unknown,
    input: {
      reason?: string;
      sourceObjectId?: string;
      radiusTarget?: number;
      lineLengthTarget?: number;
      preserveLineStartPoint?: boolean;
    } = {},
  ): void => {
    const targetUuid = typeof target === 'object' && target !== null && 'uuid' in target
      ? String((target as { uuid?: unknown }).uuid ?? '')
      : '';
    const result = sketchPropertyEditService.finalizeEdit({
      target: target as never,
      reason: input.reason ?? 'property-edit',
      sourceObjectId: input.sourceObjectId ?? (targetUuid || undefined),
      ...(input.radiusTarget !== undefined ? { radiusTarget: input.radiusTarget } : {}),
      ...(input.lineLengthTarget !== undefined ? { lineLengthTarget: input.lineLengthTarget } : {}),
      ...(input.preserveLineStartPoint !== undefined
        ? {preserveLineStartPoint: input.preserveLineStartPoint}
        : {}),
    });
    if (!result.solved) {
      sketchPropertyHistorySessionService.rollbackCurrentEdit({
        target,
        label: SKETCH_PROPERTY_HISTORY_LABEL,
      });
      options.getViewer()?.interactionStateManager?.requestVisualRefresh?.();
      return;
    }
    sketchPropertyHistorySessionService.commitAfterEdit({
      target,
      ownerSketchGroupId: result.ownerSketchGroupId,
      label: SKETCH_PROPERTY_HISTORY_LABEL,
      commandType: SKETCH_PROPERTY_HISTORY_COMMAND_TYPE,
    });
    options.getViewer()?.interactionStateManager?.requestVisualRefresh?.();
  };

  const buildPropertyManagerContext = () => {
    const currentDrawingTool = options.currentDrawingTool.value;
    const shouldHideEmptyMessage = options.isDrawingMode.value
      && currentDrawingTool === SketchDrawingTool.StyleSpline
      && (options.drawingPointCount.value ?? 0) > 0;
    return {
    activeCommand: options.activeOperationCommand.value,
    drawingTool: currentDrawingTool ? String(currentDrawingTool) : null,
    currentDrawingCommand: options.currentDrawingCommand.value,
    readonlyPreview: options.readonlyPreview.value,
    hideEmptyMessage: shouldHideEmptyMessage,
    isDrawingMode: options.isDrawingMode.value,
    isSketchActive: options.isSketchActive.value,
    datumPlaneId: options.currentDrawingDatumPlaneId.value,
    selectedObject: options.selectedObject.value,
    dialog: options.dialog,
    renderer: options.getViewer(),
    sketchDimensionSelection: options.sketchDimensionSelection?.value ?? null,
    prepareSketchPropertyEdit,
    finalizeSketchPropertyEdit,
    removeSketchRelation: options.removeSketchRelation,
    clearSelection: options.clearSelection,
    onCancel: options.handlePropertyManagerCancel,
    onConfirm: options.handlePropertyManagerConfirm,
    onHelp: options.handlePropertyManagerHelp,
    onPreviewToggle: options.handlePropertyManagerPreviewToggle,
    ...(options.blendContext?.value ?? {}),
    ...(options.shellContext?.value ?? {}),
    ...(options.ribContext?.value ?? {}),
    onSwitchActiveCommand: options.handleSwitchActiveCommand,
    // 拉伸会话已提供完整纯数据投影，不能二次挑选字段而截断Worker能力和目标选择状态。
    extrudeState: { ...options.extrudeUiState.value },
    extrudeActive: options.extrudeUiState.value.active,
    extrudeOperation: options.extrudeUiState.value.operation,
    extrudeDepth: options.extrudeUiState.value.depth,
    extrudeDirection: options.extrudeUiState.value.direction,
    extrudeMergeResult: options.extrudeUiState.value.mergeResult,
    extrudeSelectedContours: options.extrudeUiState.value.selectedContours,
    extrudeCanSubmit: options.extrudeUiState.value.canSubmit,
    extrudePreviewError: options.extrudeUiState.value.previewError,
    submitExtrude: options.submitExtrude,
    cancelExtrude: options.cancelExtrude,
    setExtrudeDepth: options.setExtrudeDepth,
    setExtrudeDirection: options.setExtrudeDirection,
    setExtrudeDirection1: options.setExtrudeDirection1,
    setExtrudeEndTargetInputActive: options.setExtrudeEndTargetInputActive,
    setExtrudeEndTargetVisual: options.setExtrudeEndTargetVisual,
    setExtrudeDirection2: options.setExtrudeDirection2,
    setExtrudeCutOptions: options.setExtrudeCutOptions,
    setExtrudeThinFeature: options.setExtrudeThinFeature,
    setExtrudeMergeResult: options.setExtrudeMergeResult,
    setExtrudeSelectedContoursInputActive: options.setExtrudeSelectedContoursInputActive,
    removeExtrudeSelectedContour: options.removeExtrudeSelectedContour,
    highlightExtrudeSelectedContour: options.highlightExtrudeSelectedContour,
    revolveState: {
      active: options.revolveUiState.value.active,
      editingFeatureId: options.revolveUiState.value.editingFeatureId,
      direction1Angle: options.revolveUiState.value.definition?.direction1Angle ?? Math.PI * 2,
      direction2Enabled: options.revolveUiState.value.definition?.direction2Enabled ?? false,
      direction2Angle: options.revolveUiState.value.definition?.direction2Angle ?? 0,
      directionReversed: options.revolveUiState.value.definition?.directionReversed ?? false,
      selectedContours: options.revolveUiState.value.selectedContours,
      selectedAxisLabel: options.revolveUiState.value.selectedAxisLabel,
      pickerTarget: options.revolveUiState.value.pickerTarget,
      canSubmit: options.revolveUiState.value.canSubmit,
      previewError: options.revolveUiState.value.previewError,
    },
    revolveActive: options.revolveUiState.value.active,
    revolveSelectedContours: options.revolveUiState.value.selectedContours,
    revolveSelectedAxisLabel: options.revolveUiState.value.selectedAxisLabel,
    revolveDirectionReversed: options.revolveUiState.value.definition?.directionReversed ?? false,
    revolveDirection1Angle: options.revolveUiState.value.definition?.direction1Angle ?? Math.PI * 2,
    revolveDirection2Enabled: options.revolveUiState.value.definition?.direction2Enabled ?? false,
    revolveDirection2Angle: options.revolveUiState.value.definition?.direction2Angle ?? 0,
    revolvePickerTarget: options.revolveUiState.value.pickerTarget,
    submitRevolve: options.submitRevolve,
    cancelRevolve: options.cancelRevolve,
    activateRevolvePicker: options.activateRevolvePicker,
    clearRevolveSelectedAxis: options.clearRevolveSelectedAxis,
    clearRevolveSelectedContours: options.clearRevolveSelectedContours,
    removeRevolveSelectedContour: options.removeRevolveSelectedContour,
    highlightRevolveSelectedAxis: options.highlightRevolveSelectedAxis,
    highlightRevolveSelectedContour: options.highlightRevolveSelectedContour,
    setRevolveDirection1Angle: options.setRevolveDirection1Angle,
    setRevolveDirection2Enabled: options.setRevolveDirection2Enabled,
    setRevolveDirection2Angle: options.setRevolveDirection2Angle,
    setRevolveDirectionReversed: options.setRevolveDirectionReversed,
    revolveCutState: {
      active: options.revolveCutUiState.value.active,
      editingFeatureId: options.revolveCutUiState.value.editingFeatureId,
      direction1Angle: options.revolveCutUiState.value.definition?.direction1Angle ?? Math.PI * 2,
      direction2Enabled: options.revolveCutUiState.value.definition?.direction2Enabled ?? false,
      direction2Angle: options.revolveCutUiState.value.definition?.direction2Angle ?? 0,
      directionReversed: options.revolveCutUiState.value.definition?.directionReversed ?? false,
      selectedContours: options.revolveCutUiState.value.selectedContours,
      selectedAxisLabel: options.revolveCutUiState.value.selectedAxisLabel,
      pickerTarget: options.revolveCutUiState.value.pickerTarget,
      canSubmit: options.revolveCutUiState.value.canSubmit,
      previewError: options.revolveCutUiState.value.previewError,
      flipSideToCut: options.revolveCutUiState.value.flipSideToCut === true,
      cutFragments: options.revolveCutUiState.value.cutFragments ?? [],
      cutDeletedSolidIds: options.revolveCutUiState.value.cutDeletedSolidIds ?? [],
      keepBodiesDialog: options.revolveCutUiState.value.keepBodiesDialog,
    },
    revolveCutActive: options.revolveCutUiState.value.active,
    submitRevolveCut: options.submitRevolveCut,
    cancelRevolveCut: options.cancelRevolveCut,
    activateRevolveCutPicker: options.activateRevolveCutPicker,
    clearRevolveCutSelectedAxis: options.clearRevolveCutSelectedAxis,
    clearRevolveCutSelectedContours: options.clearRevolveCutSelectedContours,
    removeRevolveCutSelectedContour: options.removeRevolveCutSelectedContour,
    highlightRevolveCutSelectedAxis: options.highlightRevolveCutSelectedAxis,
    highlightRevolveCutSelectedContour: options.highlightRevolveCutSelectedContour,
    setRevolveCutDirection1Angle: options.setRevolveCutDirection1Angle,
    setRevolveCutDirection2Enabled: options.setRevolveCutDirection2Enabled,
    setRevolveCutDirection2Angle: options.setRevolveCutDirection2Angle,
    setRevolveCutDirectionReversed: options.setRevolveCutDirectionReversed,
    setRevolveCutFlipSideToCut: options.setRevolveCutFlipSideToCut,
    toggleRevolveCutFragment: options.toggleRevolveCutFragment,
    setRevolveCutKeepBodiesSelectionMode: options.setRevolveCutKeepBodiesSelectionMode,
    toggleRevolveCutKeepBodiesDraftFragment: options.toggleRevolveCutKeepBodiesDraftFragment,
    confirmRevolveCutKeepBodiesDialog: options.confirmRevolveCutKeepBodiesDialog,
    cancelRevolveCutKeepBodiesDialog: options.cancelRevolveCutKeepBodiesDialog,
    referenceAxisActive: options.referenceAxisActive.value,
    submitReferenceAxis: options.submitReferenceAxis,
    cancelReferenceAxis: options.cancelReferenceAxis,
    referencePlaneActive: options.referencePlaneActive.value,
    submitReferencePlane: options.submitReferencePlane,
    cancelReferencePlane: options.cancelReferencePlane,
    referencePointActive: options.referencePointActive.value,
    submitReferencePoint: options.submitReferencePoint,
    cancelReferencePoint: options.cancelReferencePoint,
    assemblyReferenceAxisActive: options.assemblyReferenceAxisActive.value,
    submitAssemblyReferenceAxis: options.submitAssemblyReferenceAxis,
    cancelAssemblyReferenceAxis: options.cancelAssemblyReferenceAxis,
    assemblyReferencePlaneActive: options.assemblyReferencePlaneActive.value,
    submitAssemblyReferencePlane: options.submitAssemblyReferencePlane,
    cancelAssemblyReferencePlane: options.cancelAssemblyReferencePlane,
    assemblyReferencePointActive: options.assemblyReferencePointActive.value,
    submitAssemblyReferencePoint: options.submitAssemblyReferencePoint,
    cancelAssemblyReferencePoint: options.cancelAssemblyReferencePoint,
    sketchCornerMode: options.sketchCornerMode.value,
    sketchCornerEnable: options.sketchCornerEnable.value,
    setSketchCornerMode: options.setSketchCornerMode,
    sketchTrimMode: options.sketchTrimMode.value,
    sketchTrimOptions: options.sketchTrimOptions.value,
    selectedSketchTrimEntity: options.selectedSketchTrimEntity.value,
    setSketchTrimMode: options.setSketchTrimMode,
    setSketchTrimOptions: options.setSketchTrimOptions,
    //------ 等距实体业务流程 开始------
    sketchOffsetDistance: options.sketchOffsetDistance.value,
    sketchOffsetReverse: options.sketchOffsetReverse.value,
    sketchOffsetBidirectional: options.sketchOffsetBidirectional.value,
    sketchOffsetAddSize: options.sketchOffsetAddSize.value,
    sketchOffsetSelectChain: options.sketchOffsetSelectChain.value,
    sketchOffsetCover: options.sketchOffsetCover.value,
    sketchOffsetBasicGeometry: options.sketchOffsetBasicGeometry.value,
    sketchOffsetOffsetGeometry: options.sketchOffsetOffsetGeometry.value,
    setSketchOffsetDistance: options.setSketchOffsetDistance,
    setSketchOffsetReverse: options.setSketchOffsetReverse,
    setSketchOffsetBidirectional: options.setSketchOffsetBidirectional,
    setSketchOffsetAddSize: options.setSketchOffsetAddSize,
    setSketchOffsetSelectChain: options.setSketchOffsetSelectChain,
    setSketchOffsetCover: options.setSketchOffsetCover,
    setSketchOffsetBasicGeometry: options.setSketchOffsetBasicGeometry,
    setSketchOffsetOffsetGeometry: options.setSketchOffsetOffsetGeometry,
    clearSketchOffsetWorkflow: options.clearSketchOffsetWorkflow,
    generateAndExitSketchOffsetWorkflow: options.generateAndExitSketchOffsetWorkflow,
    //------ 等距实体业务流程 结束------
    convertEntitiesState: options.convertEntitiesState.value,
    confirmConvertEntities: options.confirmConvertEntities,
    cancelConvertEntities: options.cancelConvertEntities,
    removeConvertEntitiesSelection: options.removeConvertEntitiesSelection,
    setConvertEntitiesAsConstruction: options.setConvertEntitiesAsConstruction,
    setConvertEntitiesSelectChain: options.setConvertEntitiesSelectChain,
    // 内环动作必须随属性面板上下文显式转发，否则按钮会因可选调用而静默失效。
    setConvertEntitiesInnerLoopsOneByOne: options.setConvertEntitiesInnerLoopsOneByOne,
    selectAllConvertEntitiesInnerLoops: options.selectAllConvertEntitiesInnerLoops,
    //------ 草图实体变换 UI 工作流 开始------
    sketchEntitiesTransformState: options.sketchEntitiesTransformState.value,
    setSketchEntitiesTransformInputMode: options.setSketchEntitiesTransformInputMode,
    setSketchEntitiesTransformSelectionRole: options.setSketchEntitiesTransformSelectionRole,
    setSketchEntitiesTransformTargetCoordinate: options.setSketchEntitiesTransformTargetCoordinate,
    setSketchEntitiesTransformAngle: options.setSketchEntitiesTransformAngle,
    syncSketchEntitiesTransformState: options.syncSketchEntitiesTransformState,
    removeSketchEntitiesTransformSelection: options.removeSketchEntitiesTransformSelection,
    repeatSketchEntitiesTransform: options.repeatSketchEntitiesTransform,
    confirmSketchEntitiesTransform: options.confirmSketchEntitiesTransform,
    cancelSketchEntitiesTransform: options.cancelSketchEntitiesTransform,
    //------ 草图实体变换 UI 工作流 结束------
    lineDrawingPointCount: options.lineDrawingPointCount.value,
    drawingPointCount: options.drawingPointCount.value,
    multipleEntitiesBoxSelected: options.multipleEntitiesBoxSelected.value,
    multipleSelectedObjects: options.multipleSelectedObjects.value,
    multipleSelectedObjectIds: options.multipleSelectedObjectIds.value,
    multipleSelectedIsCrossing: options.multipleSelectedIsCrossing.value,
    linePropertyVisible: options.linePropertyVisible.value,
    arcPropertyVisible: options.arcPropertyVisible.value,
    circlePropertyVisible: options.circlePropertyVisible.value,
    ellipsePropertyVisible: options.ellipsePropertyVisible.value,
    polygonPropertyVisible: options.polygonPropertyVisible.value,
    rectanglePropertyVisible: options.rectanglePropertyVisible.value,
    splinePropertyVisible: options.splinePropertyVisible.value,
    styleSplinePropertyVisible: options.styleSplinePropertyVisible.value,
    slotPropertyVisible: options.slotPropertyVisible.value,
    pointPropertyVisible: options.pointPropertyVisible.value,
    insertLinePropertyVisible: options.insertLinePropertyVisible.value,
    insertComponentActive: options.insertComponentActive.value,
    insertComponentFiles: options.insertComponentFiles.value,
    insertComponentFilterKinds: options.insertComponentFilterKinds.value,
    insertComponentQuery: options.insertComponentQuery.value,
    insertComponentLoading: options.insertComponentLoading.value,
    insertComponentHasMore: options.insertComponentHasMore.value,
    insertComponentTotal: options.insertComponentTotal.value,
    insertComponentFixationMode: options.insertComponentFixationMode.value,
    selectedInsertComponentFiles: options.selectedInsertComponentFiles.value,
    setInsertComponentFilterKinds: options.setInsertComponentFilterKinds,
    setInsertComponentQuery: options.setInsertComponentQuery,
    setInsertComponentFixationMode: options.setInsertComponentFixationMode,
    loadMoreInsertComponents: options.loadMoreInsertComponents,
    selectInsertComponent: options.selectInsertComponent,
    toggleInsertComponentSelection: options.toggleInsertComponentSelection,
    confirmInsertComponentSelection: options.confirmInsertComponentSelection,
    cancelInsertComponent: options.cancelInsertComponent,
    //------ 线性零部件阵列入口 开始------
    confirmAssemblyLinearComponentPattern: options.confirmAssemblyLinearComponentPattern,
    cancelAssemblyLinearComponentPattern: options.cancelAssemblyLinearComponentPattern,
    //------ 线性零部件阵列入口 结束------
    };
  };

  return {
    buildPropertyManagerContext,
  };
}
